import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Truck3D from "@/components/Truck3D";
import QuoteFlow from "@/components/QuoteFlow";
import Gallery from "@/components/Gallery";
import Header, { Footer } from "@/components/SiteChrome";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Clock3, Truck, Sparkles, ArrowRight, Star } from "lucide-react";

export default function Home() {
  const [arrived, setArrived] = useState(false);
  const [driving, setDriving] = useState(true);
  const [step, setStep] = useState(0);
  const panelRef = useRef(null);
  const stageRef = useRef(null);

  useEffect(() => {
    if (arrived) {
      const t = setTimeout(() => setDriving(false), 380);
      return () => clearTimeout(t);
    }
  }, [arrived]);

  return (
    <div id="top" className="min-h-screen bg-[#F5EFE6] text-stone-900">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-grain">
        {/* Background ornaments */}
        <div className="pointer-events-none absolute inset-0 bg-dots opacity-60" />
        <div className="pointer-events-none absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-amber-100/60 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full bg-stone-200/70 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-14 pb-6">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-10 h-px bg-amber-700" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-semibold text-stone-700">
              The white-glove move · Est. 2025
            </span>
          </div>

          <div className="grid md:grid-cols-12 gap-6 items-end">
            <h1 className="md:col-span-8 font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] font-light tracking-[-0.04em] leading-[0.92] text-stone-950">
              Move with{" "}
              <span className="italic font-light text-amber-800">grace.</span>
              <br />
              Haul with{" "}
              <span className="italic font-light text-amber-800">conviction.</span>
            </h1>
            <div className="md:col-span-4 pb-3">
              <p className="text-base sm:text-lg text-stone-600 leading-relaxed font-light">
                SWIFTHAUL is a new kind of moving company. Curated crews, padded everything, and a
                single trip that handles your move <em>and</em> your cleanout.
              </p>
              <div className="mt-5 flex items-center gap-2 text-xs text-stone-700">
                <div className="flex">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-700 text-amber-700" />)}
                </div>
                <span className="font-semibold tracking-wide">4.9 · 1,200+ moves completed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Truck stage */}
        <div className="relative">
          <div className="absolute inset-x-0 top-0 h-[55%] bg-gradient-to-b from-stone-100/60 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-2">
            <div ref={stageRef} className="scene-3d relative h-[440px] sm:h-[480px] md:h-[520px] flex items-center justify-center">
              <div className="relative">
                <Truck3D
                  driving={driving}
                  onArrived={() => setArrived(true)}
                  showLogo={step === 0 || step === 4}
                  panelRef={panelRef}
                />
              </div>
            </div>
            <CardOverlay stageRef={stageRef} active={step === 1 || step === 2 || step === 3}>
              <QuoteFlow step={step} setStep={setStep} />
            </CardOverlay>

            {/* Road */}
            <div className="relative -mt-1">
              <div className="h-[3px] bg-stone-900" />
              <div className={`h-3 ${driving ? "road-stripes" : "bg-stone-300"}`} />
              <div className="h-[2px] bg-stone-900/70" />
            </div>

            {/* Begin CTA */}
            <div className="flex flex-col items-center justify-center mt-10 mb-4 min-h-[80px]">
              <AnimatePresence>
                {arrived && step === 0 && (
                  <motion.div
                    key="begin"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 14 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center"
                  >
                    <Button
                      data-testid="begin-quote-button"
                      onClick={() => setStep(1)}
                      className="group bg-stone-950 hover:bg-amber-800 text-stone-50 font-display text-sm h-14 px-9 rounded-full shadow-[0_10px_0_-2px_#1c1917] hover:shadow-[0_4px_0_-2px_#78350F] hover:translate-y-[3px] transition-all duration-300 tracking-[0.18em] uppercase"
                    >
                      Begin Your Quote
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <p className="mt-4 text-xs tracking-[0.2em] uppercase text-stone-500 font-medium">
                      ~60 seconds · No spam · Same-day responses
                    </p>
                  </motion.div>
                )}
                {arrived && (step === 1 || step === 2 || step === 3) && (
                  <motion.p
                    key="hint"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-xs tracking-[0.2em] uppercase text-stone-500 text-center"
                  >
                    Complete the form above to receive your quote
                  </motion.p>
                )}
                {step === 4 && (
                  <motion.p
                    key="thanks"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-sm font-display italic text-stone-700 text-center"
                  >
                    Thank you. A coordinator will be in touch shortly.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE TRUST STRIP */}
      <section className="border-y border-stone-900/15 bg-stone-950 text-stone-100 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4">
          {["a", "b"].map((dup) => (
            <div key={dup} className="flex items-center gap-12 px-12 shrink-0">
              {["Fully Insured", "Same-day Junk Pickup", "Padded Wrap Standard", "Flat-rate Quotes", "Donations Routed Free", "Five-star Crews"].map((t) => (
                <span key={t} className="flex items-center gap-3 text-sm tracking-[0.2em] uppercase font-light">
                  <span className="w-1 h-1 rounded-full bg-amber-500" />
                  {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-28 sm:py-36 bg-[#F5EFE6] relative overflow-hidden">
        <div className="absolute inset-0 bg-grain pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase font-semibold text-amber-700 mb-4">What we do</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] leading-[0.95] text-stone-950 max-w-2xl">
                Four services.<br />
                <span className="italic">One quiet trip.</span>
              </h2>
            </div>
            <p className="md:max-w-md text-base text-stone-600 leading-relaxed">
              Whether you're moving across town or finally clearing the basement, we've engineered a single visit
              that handles every part of it.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Local Moves", d: "Apartments, condos, homes, and single-piece furniture moves. Padded wrap, hand-truck loaded, careful unload." },
              { n: "02", t: "Long-Distance", d: "Cross-state relocations with a single dedicated crew end-to-end. No transfers." },
              { n: "03", t: "Junk Removal", d: "Cleanouts, hauls, and donation drop-offs. You pay only for the volume we take." },
              { n: "04", t: "Commercial Moving", d: "Office relocations, retail fit-outs, and warehouse transfers — scheduled around your business hours." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="group bg-stone-50 border border-stone-300/70 p-8 rounded-sm hover:border-amber-700 transition-colors duration-500"
              >
                <span className="font-display italic text-amber-700 text-lg">{s.n}</span>
                <h3 className="font-display text-2xl sm:text-3xl font-light mt-2 mb-4 text-stone-950 leading-tight tracking-[-0.02em]">
                  {s.t}
                </h3>
                <p className="text-sm text-stone-600 leading-relaxed">{s.d}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-stone-500 group-hover:text-amber-700 transition-colors">
                  Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust row */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-8 border-t border-stone-300/70 pt-12">
            <Trust icon={ShieldCheck} label="Licensed & Insured" />
            <Trust icon={Clock3} label="On-time Guarantee" />
            <Trust icon={Truck} label="Fleet of Every Size" />
            <Trust icon={Sparkles} label="No Hidden Fees" />
          </div>
        </div>
      </section>

      <Gallery />
      <Footer />
    </div>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-4">
      <span className="w-11 h-11 border border-stone-900 grid place-items-center rounded-full">
        <Icon className="w-4 h-4 text-stone-900" />
      </span>
      <span className="text-sm font-medium text-stone-900 tracking-tight">{label}</span>
    </div>
  );
}

/**
 * CardOverlay renders the quote-flow card as an elegant floating overlay.
 * - Mobile: full-screen bottom sheet (portaled to body to escape .scene-3d perspective)
 * - Desktop: floating rounded card, portaled to body, centered over the truck stage.
 *   Auto-sizes to content with no internal scroll, overlapping the truck.
 */
function CardOverlay({ stageRef, active, children }) {
  const [box, setBox] = useState(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );

  // Track viewport changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Lock body scroll while mobile modal is open
  useEffect(() => {
    if (isMobile && active) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobile, active]);

  // Desktop: track stage position so we can center the card over the truck
  useEffect(() => {
    if (!active || isMobile) return;
    const update = () => {
      const el = stageRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    const id = setTimeout(update, 60);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  }, [active, stageRef, isMobile]);

  if (!active) return null;

  // Mobile: full-screen bottom sheet
  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[100] bg-stone-950/55 backdrop-blur-sm flex items-end justify-center pointer-events-auto">
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 280 }}
          className="relative w-full max-h-[92vh] bg-stone-50 rounded-t-2xl overflow-y-auto shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.5)]"
        >
          {/* drag handle */}
          <div className="sticky top-0 z-50 flex justify-center pt-2 pb-1 bg-stone-50">
            <div className="w-10 h-1 bg-stone-300 rounded-full pointer-events-none" />
          </div>
          {children}
        </motion.div>
      </div>,
      document.body
    );
  }

  // Desktop: elegant floating card, portaled to body, centered over the truck stage
  if (!box) return null;
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: box.top + box.height / 2,
        left: box.left + box.width / 2,
        transform: "translate(-50%, -50%)",
        width: "min(760px, 92vw)",
        maxHeight: "calc(100vh - 40px)",
        zIndex: 60,
      }}
      className="pointer-events-auto overflow-y-auto rounded-2xl"
    >
      {children}
    </div>,
    document.body
  );
}
